import { Injectable } from '@angular/core';
import { Store, select } from '@ngrx/store';
import { IAppState } from '../../app.state';
import { Http } from '@angular/http';
import { Config } from '../../universal-config';
import { selectAllOrganizations } from '../../organizations/organizations.selector';
import { selectAllWorkspaces } from '../../workspaces/workspaces.selector';
import { selectCurrentShare } from '../models';
import { closeDialog, openDialog } from '../../common/dialogs/dialogs.actions';

type MentionPrimaryKey = number | string
interface MentionMapFormat1<T> { [k: string]: T }
interface MentionMapFormat2<T> { [k: number]: T }
interface MentionCacheItem { type: string, name: string, workspace_id: string }
type MentionUpdateFormat = MentionMapFormat1<string> & MentionMapFormat2<string>
type MentionCache = MentionMapFormat1<MentionCacheItem> & MentionMapFormat2<MentionCacheItem>
interface MentionMap { cache: MentionCache, currentWorkspaceId: string }

@Injectable()
export class MentionService {
  isLoggedIn;
  shareApiService;
  nimbusUrl;
  authHost;
  nimbusLoginUrl;
  organizations;
  workspaces;
  share;

  constructor(
    public _store: Store<IAppState>,
    public http: Http,
    public _config: Config,
  ) {
    this.shareApiService = _config.get('SHARE_API_SERVICE');
    this.nimbusUrl = _config.get('NIMBUS_URL');
    this.authHost = _config.get('AUTH_HOST');
    this.nimbusLoginUrl = _config.get('AUTH_LOGIN_PATH');
    const organizations$ = this._store.pipe(select(selectAllOrganizations));
    const workspaces$ = this._store.pipe(select(selectAllWorkspaces));
    const share$ = this._store.pipe(select(selectCurrentShare));
    organizations$.subscribe((organizations) => this.organizations = organizations);
    workspaces$.subscribe((workspaces) => this.workspaces = workspaces);
    share$.subscribe((share) => this.share = share.instance);
  }

  onMentionClick(objectType, objectId, embedInfo) {
    switch(objectType) {
      case 'user':
        return false;
      case 'workspace':
        this.goToWorkspace(objectId);
        break;
      case 'folder':
        this.toNode(this.share.workspaceId, objectId, true, embedInfo);
        break;
      case 'note':
        this.toNode(this.share.workspaceId, objectId, false, embedInfo)
        break;
    }
  }

  goToWorkspace(workspaceId) {
    this.http.get(`${this.shareApiService}/shares/domain/${workspaceId}`).subscribe((response) => {
      const result = response.json();
      const domainInfo = result.domainInfo;
      this.navigateToWorkspace(domainInfo, workspaceId);
    });
  }

  toNode(workspaceId, nodeId, isFolder, embedInfo, hash?) {
    this.http.get(`${this.shareApiService}/shares/bynode/${nodeId}?workspaceId=${workspaceId}`).subscribe((response) => {
      const result = response.json();
      const share = result.share;
      const note = result.note;
      const domainInfo = result.domainInfo;
      if(!share && !note) {
        this.openErrorDialog();
        return;
      }
      this.navigateToNode(share, domainInfo, workspaceId, embedInfo, nodeId, isFolder, hash);
    })
  }

  navigateToNode(share, domainInfo, workspaceId, embedInfo, nodeId?, isFolder?, hash?) {
    const domain = this.getDomainUrl(domainInfo);
    if(share) {
      this.navigate(this.getShareUrl(domain, share, embedInfo, hash), '_self');
    } else {
      const target = embedInfo.isEmbed ? '_blank' : '_self';
      if(isFolder) {
        this.navigate(this.getFolderUrl(domain, workspaceId, nodeId), target);
      } else {
        this.navigate(this.getNoteUrl(domain, workspaceId, nodeId, hash), target);
      }
    }
  }

  navigateToWorkspace(domainInfo, workspaceId) {
    const domain = this.getDomainUrl(domainInfo);
    this.navigate(`${domain}/ws/${workspaceId}`, '_blank');
  }

  getDomainUrl(domainInfo) {
    if(domainInfo) {
      if(domainInfo.domain) {
        return `https://${domainInfo.domain}`;
      } else if(domainInfo.sub) {
        return `https://${domainInfo.sub}.${this.authHost}`;
      }
    } else {
      const url = new URL(window.location.toString())
      return url.origin;
    }
  }

  getShareUrl(domain, share, embedInfo, hash?) {
    let url = `${domain}/share/${share.id}/${share.securityKey}`;
    if(embedInfo.isEmbed) {
      let embedUrl = `?embed=true`
      if(embedInfo.isEmbedHeaderHidden) embedUrl += '&hide-header=true';
      if(embedInfo.isEmbedPrintEnabled) embedUrl += '&print-enabled=true';
      if(embedInfo.isWordpress) embedUrl += '&wordpress=true';
      if(embedInfo.theme) embedUrl += '&theme=' + embedInfo.theme;
      url += embedUrl;
    }
    url += `${hash ? hash : ''}`;
    return url;
  }

  getNoteUrl(domain, workspaceId, noteId, hash?) {
    return `${domain}/ws/${workspaceId}/note/${noteId}${hash ? hash : ''}`
  }

  getFolderUrl(domain, workspaceId, folderId) {
    return `${domain}/ws/${workspaceId}/folder/${folderId}`
  }

  openErrorDialog() {
    const dialogOptions = {
      component: false,
      title: 'Error',
      text: 'This note was deleted or is inaccessible for viewing.',
      hideCancelButton: true,
      okButtonText: 'Ok',
      onSuccess: () => {
        this._store.dispatch(closeDialog());
      }
    }
    this._store.dispatch(openDialog(dialogOptions));
  }

  navigate(url, target) {
    window.open(url, target);
  }

  async getMentionDiffMap(map: MentionMap) {
    return await this.http.post('/share/share-api/mentionDiff', map).map(res => res.json()).toPromise()
  }

  async getMentionTitle (objectType: string, objectId: MentionPrimaryKey, workspaceId: string): Promise<string> {
    console.log('[editor][getMentionTitle()]:', objectId, objectType, workspaceId)
    const cacheLike: MentionCache = { [objectId]: { name: '', type: objectType, workspace_id: workspaceId } }
    const { diff: diffMap } = await this.getMentionDiffMap({ cache: cacheLike, currentWorkspaceId: this.share.workspaceId })
    return diffMap[objectId] || ''
  }

  async updateMentionTitles (realCache: MentionCache): Promise<MentionUpdateFormat> {
    console.log('[editor][updateMentionTitles()]:', realCache)
    const { diff: diffMap } = await this.getMentionDiffMap({ cache: realCache, currentWorkspaceId: this.share.workspaceId })
    return diffMap
  }

}
